import { useMemo, useState, type KeyboardEvent } from "react";
import { HelpCircle, Send, Loader2 } from "lucide-react";

/**
 * "Ask about this data" input — Feature #3. Forwards the question +
 * cached rows to the narrate endpoint; the markdown answer renders as
 * the next assistant chat turn (no new chart).
 */
const ASK_HINTS = [
  "why did Q3 dip?",
  "what's the YoY growth?",
  "which value is the outlier?",
  "what's the trend?",
  "summarise the top 3 rows",
];

interface Props {
  columnNames: string[];
  rows: Array<Array<unknown>>;
  busy: boolean;
  onSubmit: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
}

export function AskAboutDataInput({ columnNames, rows, busy, onSubmit }: Props) {
  const [draft, setDraft] = useState("");
  const placeholder = useMemo(
    () => `Ask: e.g. ${ASK_HINTS[Math.floor(Math.random() * ASK_HINTS.length)]}`,
    [],
  );
  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    onSubmit(trimmed, columnNames, rows);
    setDraft("");
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };
  return (
    <div
      role="group"
      aria-label="Ask about this data"
      className="mt-2 flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100"
    >
      <HelpCircle className="ml-1 h-3.5 w-3.5 flex-none text-slate-400" aria-hidden="true" />
      <input
        type="text"
        className="flex-1 min-w-0 border-0 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 placeholder:italic focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-slate-400"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
        aria-label="Ask about this data prompt"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || draft.trim().length === 0}
        aria-label={busy ? "Analysing…" : "Ask"}
        title={busy ? "Analysing…" : "Ask"}
        className="flex-none inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-600 text-white transition hover:-translate-y-px hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
